import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AccessiblePrice } from '@/components/AccessiblePrice'
import { Perils } from '@/components/Perils/Perils'
import { Price } from '@/components/Price'
import { type ProductOfferFragment, useOfferAddonSelectMutation } from '@/services/graphql/generated'
import * as Checkbox from '@radix-ui/react-checkbox'
import { Badge } from 'ui/src/components/Badge/Badge'
import { Button } from 'ui/src/components/Button/Button'
import { Card } from 'ui/src/components/Card/Card'
import { Dialog } from 'ui/src/components/Dialog/Dialog'
import { LargeDialog } from 'ui/src/components/Dialog/LargeDialog'
import { Text } from 'ui/src/components/Text/Text'
import { CheckIcon } from 'ui/src/icons/CheckIcon'
import { yStack } from 'ui/src/patterns/stack'
import { sprinkles } from 'ui/src/theme/sprinkles.css'
import {
  badge as badgeStyle,
  button as buttonStyle,
  description,
  item,
  title,
} from '../CardRadioGroup/CardRadioGroup.css'
import { addonDescription, addonRow, addonRowTitle, checkboxIndicator, checkboxRoot } from './CarAddonsSelector.css'

type Props = {
  offer: ProductOfferFragment
}

export function CarAddonsSelector({ offer }: Props) {
  const { t } = useTranslation(['purchase-form', 'common'])
  const [selectAddon] = useOfferAddonSelectMutation()
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const addons = offer.addons

  const isTeaser = addons.length === 0

  if (isTeaser) {
    return (
      <section>
        <Card.Root className={item} style={{ cursor: 'default' }}>
          <Card.Header>
            <Card.Heading>
              <Card.Title as="h2" className={title}>
                <Text as="span" size="xl">
                  {t('CAR_ADDON_SECTION_TITLE')}
                </Text>
              </Card.Title>
            </Card.Heading>
          </Card.Header>

          <Card.Content className={yStack({ gap: 'md' })}>
            <Text balance={true} className={description}>
              {t('CAR_ADDON_TEASER_DESCRIPTION')}
            </Text>
          </Card.Content>
        </Card.Root>
      </section>
    )
  }

  const selectedAddons = addons.filter((addon) => addon.isSelected)
  const hasSelectedAddons = selectedAddons.length > 0
  const allAddonsSelected = addons.length > 0 && selectedAddons.length === addons.length

  const displayPrice = hasSelectedAddons
    ? selectedAddons.reduce((sum, addon) => sum + addon.grossCost.amount, 0)
    : Math.min(...addons.map((addon) => addon.grossCost.amount))

  const priceAmount = {
    currencyCode: addons[0].grossCost.currencyCode,
    amount: displayPrice,
  }
  const pricePrefix = hasSelectedAddons ? '' : 'fr. '

  const handleAddonToggle = (addonId: string, isCurrentlySelected: boolean) => {
    const newSelectedIds = isCurrentlySelected
      ? selectedAddons.filter((addon) => addon.id !== addonId).map((addon) => addon.id)
      : [...selectedAddons.map((addon) => addon.id), addonId]

    selectAddon({
      variables: {
        productOfferId: offer.id,
        addonIds: newSelectedIds,
      },
      optimisticResponse: {
        productOfferAddonsSelect: {
          productOffers: [
            {
              ...offer,
              addons: offer.addons.map((addon) => ({
                ...addon,
                isSelected: newSelectedIds.includes(addon.id),
              })),
            },
          ],
        },
      },
    })
  }

  return (
    <LargeDialog.Root open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <section className={yStack({ gap: 'lg' })} style={{ isolation: 'isolate' }}>
        <Card.Root className={item} style={{ cursor: 'default' }} data-theme={allAddonsSelected ? 'dark' : 'light'}>
          <Card.Header>
            <Card.Heading>
              <Card.Title as="h2" className={title}>
                <Text as="span" size="xl">
                  {t('CAR_ADDON_SECTION_TITLE')}
                </Text>
              </Card.Title>
            </Card.Heading>
          </Card.Header>

          <Card.Aside floating>
            <Badge className={badgeStyle} variant={hasSelectedAddons ? 'green1' : 'outline'} size="medium">
              <AccessiblePrice price={priceAmount} prefix={pricePrefix} />
            </Badge>
          </Card.Aside>

          <Card.Content className={yStack({ gap: 'md' })}>
            <Text className={description}>{t('CAR_ADDON_SECTION_DESCRIPTION')}</Text>

            <div className={yStack({ gap: 'xs' })}>
              {addons.map((addon) => (
                <label key={addon.id} className={addonRow} data-theme={addon.isSelected ? 'dark' : 'light'}>
                  <Checkbox.Root
                    className={checkboxRoot}
                    checked={addon.isSelected}
                    onCheckedChange={() => handleAddonToggle(addon.id, addon.isSelected)}
                  >
                    <Checkbox.Indicator className={checkboxIndicator}>
                      <CheckIcon size="1rem" />
                    </Checkbox.Indicator>
                  </Checkbox.Root>

                  <Text className={addonRowTitle} size="md">
                    {addon.variant.displayName}
                  </Text>

                  <Text color="textTranslucentSecondary" size="xs">
                    <Price
                      amount={addon.grossCost.amount}
                      currencyCode={addon.grossCost.currencyCode}
                      showPlusIcon={true}
                    />
                  </Text>

                  <Text className={addonDescription} color="textTranslucentSecondary" size="xs">
                    {addon.description}
                  </Text>
                </label>
              ))}
            </div>

            <Dialog.Trigger asChild>
              <Button className={buttonStyle} size="medium" variant="secondary" fullWidth>
                {t('CAR_ADDON_COVERAGE_BUTTON')}
              </Button>
            </Dialog.Trigger>
          </Card.Content>
        </Card.Root>
      </section>

      <LargeDialog.Content>
        <LargeDialog.CloseIconButton aria-label={t('common:DIALOG_CLOSE')} />
        <LargeDialog.Heading as="h2">{t('CAR_ADDON_COVERAGE_DIALOG_TITLE')}</LargeDialog.Heading>
        <LargeDialog.Heading as="h3" color="textSecondary" className={sprinkles({ marginBottom: 'xl' })}>
          {t('CAR_ADDON_COVERAGE_DIALOG_SUBTITLE')}
        </LargeDialog.Heading>

        <div className={yStack({ gap: 'xl' })}>
          {addons.map((addon) => (
            <div key={addon.id}>
              <Badge as="h3" className={sprinkles({ marginBottom: 'md' })} variant="grey1" size="small">
                {addon.variant.displayName}
              </Badge>
              <Perils items={addon.variant.perils} />
            </div>
          ))}
        </div>
      </LargeDialog.Content>
    </LargeDialog.Root>
  )
}
